import { defineStore } from 'pinia'
import { ref } from 'vue'

export type ThemeMode = 'light' | 'dark'
export const SUPPORTED_LOCALES = ['en'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

/**
 * UI-only preferences (sidebar collapse, theme, locale). Deliberately not
 * server data — that's TanStack Query's job (see `lib/query-client.ts`).
 * Pinia holds session, RBAC context, and UI prefs; Vue Query owns server
 * cache/invalidation. Don't blend the two (admin-portal-specification §2).
 */
export const useUiStore = defineStore('ui', () => {
  const sidebarCollapsed = ref(false)
  // Mirrors the `[data-theme]` attribute selector used by both
  // design-tokens/tokens.css and the PrimeVue preset's darkModeSelector
  // (see plugins/primevue-theme.ts) — one source of truth for light/dark.
  const theme = ref<ThemeMode>('light')
  const locale = ref<SupportedLocale>('en')

  function toggleSidebar() {
    sidebarCollapsed.value = !sidebarCollapsed.value
  }

  function setTheme(next: ThemeMode) {
    theme.value = next
  }

  function setLocale(next: SupportedLocale) {
    locale.value = next
  }

  return { sidebarCollapsed, theme, locale, toggleSidebar, setTheme, setLocale }
})
